import { createFileRoute, notFound } from '@tanstack/react-router';
import { MarkdownPage } from '@/components/MarkdownPage';

// Legal content page (/legal/terms, /legal/privacy, /legal/cancellation).
//
// autoCodeSplitting (vite.config.ts) splits this file into an EAGER half (the
// route options — `loader` among them) and a LAZY half (the component). An
// import is only pulled into the lazy chunk if nothing in the eager half
// reaches it.
//
// OH-67: this file previously held a `PAGES` map that carried both the ?raw
// markdown and the slug validation, and the `loader` read `params.slug in
// PAGES`. That single reference dragged all three documents into the EAGER
// half — i.e. into the entry chunk every visitor downloads, whether or not
// they ever open a legal page. The stale comment here claimed the opposite,
// and nothing measured it: the texts were small enough that the budget
// absorbed them. Filling in the operator placeholders grew them past the
// 205 KB entry budget and the gate finally said so (206.7 KB), which is the
// gate doing its job — the waste predates this task by months.
//
// So the loader now validates against a bare string list, and the markdown is
// reachable ONLY from the component. Measured on the same build the gate
// reads: entry 206.7 KB → 200.2 KB, and `legal._slug`'s own chunk 0.3 KB →
// 7.0 KB. The documents did not shrink; they moved off the critical path.
import terms from '@/content/legal/terms.md?raw';
import privacy from '@/content/legal/privacy.md?raw';
import cancellation from '@/content/legal/cancellation.md?raw';

const SLUGS = ['terms', 'privacy', 'cancellation'] as const;
type Slug = (typeof SLUGS)[number];

const isSlug = (v: string): v is Slug => (SLUGS as readonly string[]).includes(v);

export const Route = createFileRoute('/legal/$slug')({
  // Validate the slug eagerly so unknown slugs render the root
  // notFoundComponent rather than a blank page. Note this deliberately does
  // NOT consult the documents themselves — see the comment above.
  loader: ({ params }) => {
    if (!isSlug(params.slug)) {
      throw notFound();
    }
    return { slug: params.slug };
  },
  component: LegalPage,
});

function LegalPage() {
  const { slug } = Route.useParams();
  if (!isSlug(slug)) {
    // Should not reach here (loader throws notFound first), but TypeScript
    // needs the guard.
    return null;
  }
  const page = PAGES[slug];
  return (
    <MarkdownPage
      title={page.title}
      description={page.description}
      content={page.content}
    />
  );
}

// What decides the split is the reference graph, not where these lines sit in
// the file — imports are hoisted, so moving them around changes nothing. PAGES
// is reachable only from LegalPage, and that is the whole fix.
const PAGES: Record<Slug, { title: string; description: string; content: string }> = {
  terms: {
    title: 'Terms and Conditions',
    description: 'Terms and conditions for booking through ollio.',
    content: terms,
  },
  privacy: {
    title: 'Privacy Policy',
    description: 'How we collect, use, and protect your personal data.',
    content: privacy,
  },
  cancellation: {
    title: 'Cancellation Policy',
    description: 'Our cancellation and refund policy.',
    content: cancellation,
  },
};
